/**
 * Действия полей экрана, рождённые вне createSlice фасада (WEB-3128, перенос
 * Slimkit, группа 1): тон картинки фона (решение владельца №3) и флаги шапки
 * и нижней кнопки. Приём — как у `./actions.ts` (WEB-2552, заход Р2):
 * отдельный createAction, чтобы фасад editorSlice.ts не рос (храповик длины
 * FACADE_MAX_LINES в store/__tests__/editorSlice.test.ts). Фасад
 * реэкспортирует создатели под прежними именами и ловит их в extraReducers
 * через `addScreenFieldCases`. Строковые имена действий прежние байт в байт
 * (`editor/setScreenBackgroundImageTone`, `editor/setScreenChromeFlags`).
 * Тела — `./screenImageTone.ts` и `./screenChromeFlags.ts`; одно нажатие —
 * один шаг «Отменить» (withHistoryPayload).
 */
import { createAction } from '@reduxjs/toolkit';
import type { ActionReducerMapBuilder } from '@reduxjs/toolkit';
import type { EditorState } from './state';
import { withHistoryPayload } from './history';
import {
  setScreenImageToneReducer,
  type ScreenImageTonePayload,
} from './screenImageTone';
import {
  setScreenChromeFlagsReducer,
  type ScreenChromeFlagsPatch,
} from './screenChromeFlags';
import { setQuizHeaderReducer, type QuizHeaderPatch } from './quizHeader';
import { setQuizCtaReducer, type QuizCtaPatch } from './quizCta';
import { setQuizLoaderReducer, type QuizLoaderPatch } from './quizLoader';
import {
  setBlockOptionIconReducer,
  type BlockOptionIconPayload,
} from './optionIcon';
import {
  setBlockOptionDescriptionReducer,
  type BlockOptionDescriptionPayload,
} from './optionDescription';
import {
  setBlockOptionNextScreenReducer,
  type BlockOptionNextScreenPayload,
} from './optionNextScreen';
import {
  setContentAndLogicReducer,
  type ContentAndLogicPayload,
} from './logicEdit';
// WEB-3718: свои шрифты и боковое поле экрана (`./funnelSettingsActions.ts`) —
// регистрируются этой же дверью и реэкспортируются фасадом через этот модуль:
// фасад не вырос ни на строку (храповик длины).
import { addFunnelSettingsCases } from './funnelSettingsActions';
// WEB-3765: удаление экрана из «Слоёв» с перешивкой переходов — та же дверь.
import {
  removeScreenRelinked,
  removeScreenRelinkedReducer,
} from './removeScreenRelinked';
export {
  addCustomFontFile,
  removeCustomFont,
  setFunnelFontId,
  setScreenPaddingX,
  takePackColors,
} from './funnelSettingsActions';

export const setScreenBackgroundImageTone = createAction<
  ScreenImageTonePayload,
  'editor/setScreenBackgroundImageTone'
>('editor/setScreenBackgroundImageTone');

export const setScreenChromeFlags = createAction<
  { screenId: string; patch: ScreenChromeFlagsPatch },
  'editor/setScreenChromeFlags'
>('editor/setScreenChromeFlags');

/**
 * Шапка квиза своими полями (слово владельца 20.10.2026) — поле настроек
 * воронки, не экрана, но тот же приём выноса из фасада (тело — `./quizHeader.ts`).
 */
export const setQuizHeader = createAction<
  QuizHeaderPatch | null,
  'editor/setQuizHeader'
>('editor/setQuizHeader');

/** Главная кнопка квиза своими полями (слово владельца 21.10.2026) — тот же приём (`./quizCta.ts`). */
export const setQuizCta = createAction<QuizCtaPatch | null, 'editor/setQuizCta'>(
  'editor/setQuizCta',
);

/** Вид загрузчика своими полями (слово владельца 21.10.2026) — тот же приём (`./quizLoader.ts`). */
export const setQuizLoader = createAction<
  QuizLoaderPatch | null,
  'editor/setQuizLoader'
>('editor/setQuizLoader');

/**
 * Значок одного варианта ответа (задание stylepack/option-grid) — тот же
 * приём выноса из фасада (тело — `./optionIcon.ts`).
 */
export const setBlockOptionIcon = createAction<
  BlockOptionIconPayload,
  'editor/setBlockOptionIcon'
>('editor/setBlockOptionIcon');

/**
 * Описание одного варианта ответа (задание stylepack/option-description) —
 * тот же приём выноса из фасада (тело — `./optionDescription.ts`).
 */
export const setBlockOptionDescription = createAction<
  BlockOptionDescriptionPayload,
  'editor/setBlockOptionDescription'
>('editor/setBlockOptionDescription');

/**
 * Куда ведёт один вариант ответа (WEB-1810; решение владельца 22.10.2026
 * ~22:40 — и у одиночного выбора картинкой) — тот же приём выноса из фасада
 * (тело — `./optionNextScreen.ts`), имя прежнее байт в байт.
 */
export const setBlockOptionNextScreen = createAction<
  BlockOptionNextScreenPayload,
  'editor/setBlockOptionNextScreen'
>('editor/setBlockOptionNextScreen');

/**
 * Экраны и правила одной правкой истории (WEB-4088, решение 30.10 Л5: окошко
 * правила у открытого экрана) — тот же приём выноса (тело — `./logicEdit.ts`).
 */
export const setContentAndLogic = createAction<
  ContentAndLogicPayload,
  'editor/setContentAndLogic'
>('editor/setContentAndLogic');

/** Регистрирует действия в extraReducers фасада; возвращает тот же builder для цепочки. */
export function addScreenFieldCases(
  builder: ActionReducerMapBuilder<EditorState>,
): ActionReducerMapBuilder<EditorState> {
  return addFunnelSettingsCases(builder)
    .addCase(
      setScreenBackgroundImageTone,
      withHistoryPayload(setScreenImageToneReducer),
    )
    .addCase(
      setScreenChromeFlags,
      withHistoryPayload(setScreenChromeFlagsReducer),
    )
    .addCase(setQuizHeader, withHistoryPayload(setQuizHeaderReducer))
    .addCase(setQuizCta, withHistoryPayload(setQuizCtaReducer))
    .addCase(setQuizLoader, withHistoryPayload(setQuizLoaderReducer))
    .addCase(
      setBlockOptionIcon,
      withHistoryPayload(setBlockOptionIconReducer),
    )
    .addCase(
      setBlockOptionDescription,
      withHistoryPayload(setBlockOptionDescriptionReducer),
    )
    .addCase(
      setBlockOptionNextScreen,
      withHistoryPayload(setBlockOptionNextScreenReducer),
    )
    .addCase(
      removeScreenRelinked,
      withHistoryPayload(removeScreenRelinkedReducer),
    )
    .addCase(
      setContentAndLogic,
      withHistoryPayload(setContentAndLogicReducer),
    );
}
